import { Checkbox, Switcher } from '@shared/ui/kit';
import type { DatePickerInputStatus } from '@shared/ui/kit/date-picker/model/types';
import { Select, type SelectValue } from '@shared/ui/select';
import { Alert, ALERT_VARIANTS } from '@features/modal-invitation/ui/alert';
import clsx from 'clsx';
import { EmailSubjectField } from '../shared/email-subject-field';
import { LockedSelectField } from '../shared/locked-select-field';
import { TicketDateField } from './components/ticket-date-field';
import { TicketTextField } from './components/ticket-text-field';
import { TicketPreview } from './components/ticket-preview';
import { TICKET_CHANNEL_SELECT_OPTIONS } from '../../lib/invitation-channel-options';
import styles from './ticket-settings-section.module.scss';

export interface TicketSettingsValues {
  isTicketSettingsCollapsed: boolean;
  isSkipTicketSendChecked: boolean;
  ticketDate: Date | null;
  selectedTicketChannels: SelectValue[];
  isSmsTicketMode: boolean;
  isBothTicketMode: boolean;
  isEmailTicketMode: boolean;
  ticketSmsText: string;
  ticketText: string;
  ticketSubject: string;
  ticketTextParts: number;
  previewLink: string;
}

export interface TicketSettingsValidation {
  ticketDatePickerStatus?: DatePickerInputStatus;
  ticketDatePickerInputMessage?: string;
  ticketBottomAlerts?: string[];
  showImmediateSendInfoAlert?: boolean;
  isRepeatSending?: boolean;
  ticketTypeError?: string | null;
  ticketTextError?: string | null;
}

export interface TicketSettingsActions {
  onToggleSettingsCollapsed: () => void;
  onToggleSkipTicketSend: () => void;
  onTicketDateChange: (dates: [Date | null, Date | null]) => void;
  onTicketChannelsChange: (values: SelectValue | SelectValue[]) => void;
  onTicketSubjectChange: (value: string) => void;
  onTicketTextChange: (value: string) => void;
}

interface TicketSettingsSectionProps {
  values: TicketSettingsValues;
  validation: TicketSettingsValidation;
  actions: TicketSettingsActions;
}

export const TicketSettingsSection = ({ values, validation, actions }: TicketSettingsSectionProps) => {
  const {
    isTicketSettingsCollapsed,
    isSkipTicketSendChecked,
    ticketDate,
    selectedTicketChannels,
    isSmsTicketMode,
    isBothTicketMode,
    isEmailTicketMode,
    ticketSmsText,
    ticketText,
    ticketSubject,
    ticketTextParts,
    previewLink,
  } = values;

  const {
    ticketDatePickerStatus = 'default',
    ticketDatePickerInputMessage,
    ticketBottomAlerts = [],
    showImmediateSendInfoAlert = false,
    isRepeatSending = false,
    ticketTypeError,
    ticketTextError,
  } = validation;

  const {
    onToggleSettingsCollapsed,
    onToggleSkipTicketSend,
    onTicketDateChange,
    onTicketChannelsChange,
    onTicketSubjectChange,
    onTicketTextChange,
  } = actions;

  const isLocked = isRepeatSending;
  const isTicketDateDisabled = isLocked || isSkipTicketSendChecked;

  return (
    <section className={styles.settingsCard}>
      <div className={styles.settingsCardTitleRow}>
        <h2 className={styles.settingsCardTitle}>Настройка билетов</h2>
        <div className={styles.ticketSkipHeader}>
          <div className={clsx(styles.ticketSkipHeaderText, isLocked && styles.ticketSkipHeaderTextDisabled)}>
            Не отправлять билет
          </div>
          <Switcher
            size="small"
            isActive={isTicketSettingsCollapsed}
            isDisabled={isLocked}
            changeFunction={onToggleSettingsCollapsed}
          />
        </div>
      </div>
      {!isTicketSettingsCollapsed && (
        <div className={styles.settingsCardContent}>
          <div className={styles.settingsCardFormBlock}>
            <TicketDateField
              isTicketDateDisabled={isTicketDateDisabled}
              isSkipTicketSendChecked={isSkipTicketSendChecked}
              isLocked={isLocked}
              ticketDate={ticketDate}
              ticketDatePickerStatus={ticketDatePickerStatus}
              ticketDatePickerInputMessage={ticketDatePickerInputMessage}
              onTicketDateChange={onTicketDateChange}
            />

            <div className={styles.ticketCheckboxRow}>
              <Checkbox isChecked={isSkipTicketSendChecked} onChange={onToggleSkipTicketSend} isDisabled={isLocked} />
              <div className={styles.ticketCheckboxText}>Отправить билет сразу после подтверждения приглашения</div>
            </div>

            {isLocked ? (
              <LockedSelectField
                label="Тип отправки"
                value={
                  TICKET_CHANNEL_SELECT_OPTIONS.filter((o) => selectedTicketChannels.includes(o.value))
                    .map((o) => o.name)
                    .join(', ') || '-'
                }
              />
            ) : (
              <div className={styles.ticketTypeField}>
                <Select
                  label="Тип отправки"
                  placeholder="Выбрать тип"
                  options={TICKET_CHANNEL_SELECT_OPTIONS}
                  selectedValue={selectedTicketChannels}
                  onChange={onTicketChannelsChange}
                  isWithChips
                />
                {ticketTypeError && <span className={styles.fieldError}>{ticketTypeError}</span>}
              </div>
            )}

            {isEmailTicketMode && (
              <EmailSubjectField
                label="Тема билета"
                subject={ticketSubject}
                isLocked={isLocked}
                onSubjectChange={onTicketSubjectChange}
              />
            )}

            <TicketTextField
              isSmsTicketMode={isSmsTicketMode}
              isBothTicketMode={isBothTicketMode}
              isLocked={isLocked}
              ticketSmsText={ticketSmsText}
              ticketText={ticketText}
              ticketTextParts={ticketTextParts}
              ticketTextError={ticketTextError}
              onTicketTextChange={onTicketTextChange}
            />
          </div>
          <TicketPreview isSmsTicketMode={isSmsTicketMode} ticketText={ticketText} previewLink={previewLink} />
        </div>
      )}
      {(showImmediateSendInfoAlert || !!ticketBottomAlerts.length) && (
        <div className={styles.alertsWrap}>
          {showImmediateSendInfoAlert && (
            <Alert variant={ALERT_VARIANTS.INFO}>
              Если не выбрать дату отправки билета, то билеты будут отправлены сразу после подтверждения приглашения
            </Alert>
          )}
          {ticketBottomAlerts.map((alertText) => (
            <Alert key={alertText} variant={ALERT_VARIANTS.WARNING}>
              {alertText}
            </Alert>
          ))}
        </div>
      )}
    </section>
  );
};
